import { LocaleProvider } from '@chakra-ui/react';
import { lazy, Suspense, useState } from 'react';
import { useTheme } from 'next-themes';
import { RawIntlProvider, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import {
  isAccountSettingsPane,
  ACCOUNT_SETTINGS_PARAM,
  type AccountSettingsPaneId,
} from './account-settings';
import { useFeatureGate } from '@/hooks';
import { FeatureFlags } from '@/lib/feature-flag-constants';

const ProfileSettingsDialog = lazy(async () => {
  const module = await import('@/screens/profile/ProfileSettingsDialog');
  return { default: module.ProfileSettingsDialog };
});

export function AccountSettingsDialogHost() {
  const [searchParams] = useSearchParams();
  const enabled = useFeatureGate(FeatureFlags.PROFILE_LAYOUT);
  const pane = searchParams.get(ACCOUNT_SETTINGS_PARAM);

  if (!enabled || !isAccountSettingsPane(pane)) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <StableSettingsDialog pane={pane} />
    </Suspense>
  );
}

function StableSettingsDialog({ pane }: { pane: AccountSettingsPaneId }) {
  const currentIntl = useIntl();
  const { resolvedTheme } = useTheme();
  const [dialogIntl] = useState(currentIntl);
  const [dialogTheme] = useState<'light' | 'dark'>(() =>
    resolvedTheme === 'dark' ||
    (resolvedTheme === undefined && typeof document !== 'undefined' && document.documentElement.classList.contains('dark'))
      ? 'dark'
      : 'light',
  );

  return (
    <RawIntlProvider value={dialogIntl}>
      <LocaleProvider locale={dialogIntl.locale}>
        <ProfileSettingsDialog pane={pane} dialogTheme={dialogTheme} />
      </LocaleProvider>
    </RawIntlProvider>
  );
}
